import React, { useMemo } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Stack,
  Typography,
} from '@mui/material';
import {
  Footprints,
  Heart,
  Scale,
  Activity,
  Apple,
  Beaker,
  Pill,
} from 'lucide-react';
import { endOfWeek, format, isToday, parseISO, startOfWeek, isWithinInterval, formatDistanceToNow } from 'date-fns';
import { DatabaseSchema, SectionValue, WalkEntry } from '../../types';

interface Props {
  database: DatabaseSchema | null;
  onNavigate: (section: SectionValue, subTab?: string) => void;
  onQuickLogWalk: () => Promise<void>;
}

const VitalTile: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: string;
  meta?: string;
  onClick?: () => void;
  status?: 'ok' | 'warn' | 'muted';
}> = ({ icon, label, value, meta, onClick, status = 'ok' }) => (
  <Card
    onClick={onClick}
    sx={{
      cursor: onClick ? 'pointer' : 'default',
      borderLeft: '3px solid',
      borderLeftColor: status === 'warn' ? 'warning.main' : status === 'muted' ? 'divider' : 'primary.main',
      flex: 1,
      minWidth: 140,
    }}
  >
    <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
        {icon}
        <Typography variant="caption" color="text.secondary">{label}</Typography>
      </Stack>
      <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.1 }}>{value}</Typography>
      {meta && <Typography variant="caption" color="text.secondary">{meta}</Typography>}
    </CardContent>
  </Card>
);

export const Today: React.FC<Props> = ({ database, onNavigate, onQuickLogWalk }) => {
  const todayStr = format(new Date(), 'yyyy-MM-dd');

  const latestWeight = useMemo(() => {
    const weights = database?.weights ?? [];
    if (!weights.length) return null;
    return [...weights].sort((a, b) => b.date.localeCompare(a.date))[0];
  }, [database?.weights]);

  const latestBP = useMemo(() => {
    const bps = database?.bloodPressure ?? [];
    if (!bps.length) return null;
    return [...bps].sort((a, b) => b.date.localeCompare(a.date))[0];
  }, [database?.bloodPressure]);

  const latestMeasurement = useMemo(() => {
    const ms = database?.bodyMeasurements ?? [];
    if (!ms.length) return null;
    return [...ms].sort((a, b) => b.date.localeCompare(a.date))[0];
  }, [database?.bodyMeasurements]);

  const height = database?.preferences?.height ?? latestWeight?.height ?? 168.5;
  const waistHeightRatio = latestMeasurement?.measurements.waist
    ? Math.round((latestMeasurement.measurements.waist / height) * 100) / 100
    : null;

  const weekWalks = useMemo(() => {
    const now = new Date();
    const start = startOfWeek(now, { weekStartsOn: 1 });
    const end = endOfWeek(now, { weekStartsOn: 1 });
    return (database?.walks ?? []).filter((w) => {
      try {
        return isWithinInterval(parseISO(w.date), { start, end });
      } catch {
        return false;
      }
    });
  }, [database?.walks]);

  const weekMinutes = weekWalks.reduce((s: number, w: WalkEntry) => s + (w.durationMin || 0), 0);
  const target = database?.preferences?.weeklyActivityMinutesTarget ?? 150;
  const targetMode = database?.preferences?.targetMode ?? 'weekly_minutes';
  const walkedToday = weekWalks.some((w) => isToday(parseISO(w.date)));

  const todayFoodLog = (database?.foodLog ?? []).find((f) => f.date === todayStr);
  const fruitVegToday = todayFoodLog?.fruitVegServings ?? 0;

  const bpTarget = database?.preferences?.bpTarget ?? { systolic: 135, diastolic: 85 };
  const bpExceeds = latestBP && (latestBP.systolic > bpTarget.systolic || latestBP.diastolic > bpTarget.diastolic);

  const supplementEntryToday = (database?.supplements ?? []).find((s) => s.date === todayStr);
  const vitDTaken = supplementEntryToday?.supplements?.vitamin_d ?? false;

  const latestLabDate = useMemo(() => {
    const labs = database?.labResults ?? [];
    if (!labs.length) return null;
    return labs.reduce((acc, l) => (l.date > acc ? l.date : acc), labs[0].date);
  }, [database?.labResults]);

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h4" fontWeight={800}>Today</Typography>
        <Typography variant="body2" color="text.secondary">
          {format(new Date(), 'EEEE, d MMMM yyyy')}
        </Typography>
      </Box>

      <Typography variant="overline" color="text.secondary">Vitals</Typography>
      <Stack direction="row" spacing={1} sx={{ mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <VitalTile
          icon={<Scale size={16} />}
          label="Weight"
          value={latestWeight ? `${latestWeight.weight} kg` : '—'}
          meta={latestWeight ? `${formatDistanceToNow(parseISO(latestWeight.date), { addSuffix: true })}` : undefined}
          onClick={() => onNavigate('log', 'weight')}
        />
        <VitalTile
          icon={<Heart size={16} />}
          label="Blood pressure"
          value={latestBP ? `${latestBP.systolic}/${latestBP.diastolic}` : '—'}
          meta={latestBP ? formatDistanceToNow(parseISO(latestBP.date), { addSuffix: true }) : undefined}
          onClick={() => onNavigate('log', 'bp')}
          status={bpExceeds ? 'warn' : 'ok'}
        />
        <VitalTile
          icon={<Activity size={16} />}
          label="Waist/height"
          value={waistHeightRatio !== null ? `${waistHeightRatio}` : '—'}
          meta={waistHeightRatio !== null ? (waistHeightRatio > 0.6 ? 'high' : waistHeightRatio > 0.49 ? 'elevated' : 'healthy') : undefined}
          onClick={() => onNavigate('body', 'measurements')}
          status={waistHeightRatio !== null && waistHeightRatio > 0.6 ? 'warn' : 'ok'}
        />
        <VitalTile
          icon={<Scale size={16} />}
          label="Body fat"
          value={latestWeight?.bodyFatPercentage !== undefined ? `${latestWeight.bodyFatPercentage}%` : '—'}
          onClick={() => onNavigate('log', 'weight')}
        />
      </Stack>

      <Typography variant="overline" color="text.secondary">Today's plan</Typography>
      <Stack spacing={1.5} sx={{ mb: 3 }}>
        <Card>
          <CardContent>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
              <Footprints size={20} />
              <Typography variant="subtitle1" fontWeight={700}>Walk today</Typography>
              {walkedToday && <Chip label="done" color="success" size="small" />}
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              {walkedToday ? 'Nice one — walk logged.' : 'Default 30 min with partner.'}
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button
                variant={walkedToday ? 'outlined' : 'contained'}
                onClick={onQuickLogWalk}
                startIcon={<Footprints size={16} />}
              >
                {walkedToday ? 'Log another' : 'Quick-log 30 min'}
              </Button>
              <Button variant="text" onClick={() => onNavigate('log', 'walk')}>Customise</Button>
            </Stack>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
              <Pill size={20} />
              <Typography variant="subtitle1" fontWeight={700}>Vitamin D</Typography>
              {vitDTaken && <Chip label="taken" color="success" size="small" />}
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              GP recommended 1000–2000 IU/day. Your August level was 26 nmol/L (insufficient).
            </Typography>
            <Button variant={vitDTaken ? 'outlined' : 'contained'} onClick={() => onNavigate('log', 'supplement')}>
              {vitDTaken ? 'Edit' : 'Mark taken'}
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
              <Apple size={20} />
              <Typography variant="subtitle1" fontWeight={700}>Food log</Typography>
              <Chip label={`${fruitVegToday}/5 fruit&veg`} size="small" color={fruitVegToday >= 5 ? 'success' : 'default'} />
            </Stack>
            <Button variant="outlined" onClick={() => onNavigate('log', 'meal')}>Tick off today</Button>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
              <Heart size={20} />
              <Typography variant="subtitle1" fontWeight={700}>Log BP</Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Best of 3 readings, 3×/day during your 2-week monitoring window.
            </Typography>
            <Button variant="outlined" onClick={() => onNavigate('log', 'bp')}>Add reading</Button>
          </CardContent>
        </Card>
      </Stack>

      <Typography variant="overline" color="text.secondary">Week at a glance</Typography>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
            <Box>
              <Typography variant="body2">Active minutes this week</Typography>
              <Typography variant="h4" fontWeight={800}>{weekMinutes}<span style={{ fontSize: 14, color: '#888' }}> / {target} min</span></Typography>
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="body2">Walks</Typography>
              <Typography variant="h4" fontWeight={800}>{weekWalks.length}</Typography>
            </Box>
          </Stack>
          {targetMode === 'weekly_minutes' && (
            <Box sx={{ height: 8, backgroundColor: 'action.hover', borderRadius: 4, overflow: 'hidden' }}>
              <Box
                sx={{
                  width: `${Math.min(100, (weekMinutes / target) * 100)}%`,
                  height: '100%',
                  backgroundColor: 'primary.main',
                }}
              />
            </Box>
          )}
        </CardContent>
      </Card>

      <Typography variant="overline" color="text.secondary">Health context</Typography>
      <Card>
        <CardContent>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
            <Beaker size={20} />
            <Typography variant="subtitle1" fontWeight={700}>Labs</Typography>
          </Stack>
          {latestLabDate ? (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Last bloodwork: {formatDistanceToNow(parseISO(latestLabDate), { addSuffix: true })}. Annual panels — no update needed until next GP visit.
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>No lab results yet.</Typography>
          )}
          <Button variant="text" onClick={() => onNavigate('health', 'labs')}>View labs</Button>
        </CardContent>
      </Card>
    </Box>
  );
};
